import RelativeTimeFormatPolyfill from 'relative-time-format'

import Cache from './cache.js'
import chooseLocale from './locale.js'
import isStyleObject from './isStyleObject.js'

import getStep from './steps/getStep.js'
import getStepDenominator from './steps/getStepDenominator.js'
import getTimeToNextUpdate from './steps/getTimeToNextUpdate.js'

import {
	addLocaleData,
	getLocaleData
} from './LocaleDataStore.js'

import defaultStyle from './style/roundMinute.js'
import getStyleByName from './style/getStyleByName.js'

import { getRoundFunction } from './round.js'

export default class TimeAgo {
	/**
	 * @param {(string|string[])} locales=[] - Preferred locales (or locale).
	 * @param {boolean} [polyfill] — Pass `false` to use native `Intl.RelativeTimeFormat` and `Intl.PluralRules` instead of the polyfills.
	 */
	constructor(locales = [], { polyfill } = {}) {
		// Convert `locales` to an array.
		if (typeof locales === 'string') {
			locales = [locales]
		}

		// Choose the most appropriate locale
		// from the list of `locales` added by the user.
		// For example, new TimeAgo("en-US") -> "en".
		this.locale = chooseLocale(
			locales.concat(TimeAgo.getDefaultLocale()),
			getLocaleData
		)

		if (typeof Intl !== 'undefined') {
			// Use `Intl.NumberFormat` for formatting numbers (when available).
			if (Intl.NumberFormat) {
				this.numberFormat = new Intl.NumberFormat(this.locale)
			}
		}

		// Some people have requested the ability to use native
		// `Intl.RelativeTimeFormat` and `Intl.PluralRules`
		// instead of the polyfills.
		// https://github.com/catamphetamine/javascript-time-ago/issues/21
		if (polyfill === false) {
			this.IntlRelativeTimeFormat = Intl.RelativeTimeFormat
			this.IntlPluralRules = Intl.PluralRules
		} else {
			this.IntlRelativeTimeFormat = RelativeTimeFormatPolyfill
			this.IntlPluralRules = RelativeTimeFormatPolyfill.PluralRules
		}

		// Cache `Intl.RelativeTimeFormat` instance.
		this.relativeTimeFormatCache = new Cache()

		// Cache `Intl.PluralRules` instance.
		this.pluralRulesCache = new Cache()
	}

	/**
	 * Formats relative date/time.
	 *
	 * @param {(number|Date)} input — A `Date` or a javascript timestamp.
	 *
	 * @param {(string|object)} style — Date/time formatting style. Either one of the built-in style names or a "custom" style definition object having `steps: object[]` and `labels: string[]`.
	 *
	 * @param {number} [options.now] - Sets the current date timestamp.
	 *
	 * @param  {boolean} [options.future] — Tells how to format value `0`:
	 *         as "future" (`true`) or "past" (`false`).
	 *         Is `false` by default, but should have been `true` actually,
	 *         in order to correspond to `Intl.RelativeTimeFormat`
	 *         that uses `future` formatting for `0` unless `-0` is passed.
	 *
	 * @param {string} [options.round] — Rounding method. Overrides the style's one.
	 *
	 * @param {boolean} [options.getTimeToNextUpdate] — Pass `true` to return `[formattedDate, timeToNextUpdate]` instead of just `formattedDate`.
	 *
	 * @param {boolean} [options.getTimeToNextUpdateUncapped] — Pass `true` to not apply the workaround for `setTimeout()` bug. https://stackoverflow.com/questions/3468607/why-does-settimeout-break-for-large-millisecond-delay-values
	 *
	 * @param {function} [options.refresh] — When `refresh` function is passed, it will be automatically called with a new text when it's time to refresh the label.
	 *
	 * @return {string} The formatted relative date/time. If no eligible `step` is found, then an empty string is returned.
	 */
	format(input, style, options) {
		if (!options) {
			if (style && !isStyle(style)) {
				options = style
				style = undefined
			} else {
				options = {}
			}
		}

		if (!style) {
			style = defaultStyle
		}

		if (typeof style === 'string') {
			style = getStyleByName(style)
		}

		const timestamp = getTimestamp(input)

		// Get locale messages for this type of labels.
		// "flavour" is a legacy name for "labels".
		const { labels, labelsType } = this.getLabels(style.flavour || style.labels)

		// `round` setting could be passed as a parameter to `.format()` function
		// or be configured globally for a style.
		const round = options.round || style.round

		// A developer can pass a custom `now`, e.g. for testing purposes.
		let now
		const nowRealWhenCalled = Date.now()
		// (deprecated)
		// Legacy way was passing `now` in `style`.
		if (style.now !== undefined) {
			now = style.now
		}
		// One could pass `now` option to `.format()`.
		if (now === undefined && options.now !== undefined) {
			now = options.now
		}
		// The default `now` is `Date.now()`.
		if (now === undefined) {
			now = nowRealWhenCalled
		}

		const getTextAndTextRefreshDelayGetterFunctions = ({ now }) => {
			// how much time has passed (in seconds)
			const secondsPassed = (now - timestamp) / 1000 // in seconds

			const future = options.future || secondsPassed < 0

			const nowLabel = getNowLabel(
				labels,
				getLocaleData(this.locale).now,
				getLocaleData(this.locale).long,
				future
			)

			// (deprecated)
			//
			// `custom` – A function of `{ elapsed, time, date, now, locale }`.
			//
			// If this function returns a value, then the `.format()` call will return that value.
			// Otherwise the relative date/time is formatted as usual.
			// This feature is currently not used anywhere and is here
			// just for providing the ultimate customization point
			// in case anyone would ever need that. Prefer using
			// `steps[step].format(value, locale)` instead.
			//
			if (style.custom) {
				const text = style.custom({
					now,
					date: new Date(timestamp),
					time: timestamp,
					elapsed: secondsPassed,
					locale: this.locale
				})
				if (text !== undefined) {
					// Won't return `timeToNextUpdate` here
					// because `custom()` seems deprecated.
					return {
						getText: () => text,
						getTextRefreshDelay: () => {
							throw new Error('`getTimeToNextUpdate: true` feature is not supported by legacy "styles" that have a `custom` function')
						}
					}
				}
			}

			// Get the list of available time interval units.
			const units = getTimeIntervalMeasurementUnits(
				// Controlling `style.steps` through `style.units` seems to be deprecated:
				// create a new custom `style` instead.
				style.units,
				labels,
				nowLabel
			)

			// Choose the appropriate time measurement unit
			// and get the corresponding rounded time amount.
			const [prevStep, step, nextStep] = getStep(
				// "gradation" is a legacy name for "steps".
				// For historical reasons, "approximate" steps are used by default.
				// In the next major version, there'll be no default for `steps`.
				style.gradation || style.steps || defaultStyle.steps,
				secondsPassed,
				{ now, units, round, future, getNextStep: true }
			)

			const getText = () => {
				return this.formatDateForStep(timestamp, step, secondsPassed, {
					labels,
					labelsType,
					nowLabel,
					now,
					future,
					round
				}) || ''
			}

			// Returns the time (in milliseconds) after which the formatted date label should be refreshed.
			//
			// It will return `undefined` for a custom style
			// that doesn't meet the minimum requirements for this feature.
			// See the README for more details.
			//
			const getTextRefreshDelay = () => {
				const timeToNextUpdate = getTimeToNextUpdate(timestamp, step, {
					nextStep,
					prevStep,
					now,
					future,
					round
				})

				// `timeToNextUpdate` could be `undefined` for a custom style
				// that doesn't meet the minimum requirements for this feature.
				// See the README for more details.
				if (typeof timeToNextUpdate === 'number') {
					// `setTimeout()` function has a bug when it fires immediately
					// when the delay is longer than about `24.85` days.
					// https://stackoverflow.com/questions/3468607/why-does-settimeout-break-for-large-millisecond-delay-values
					//
					// In order to not burden the end users of this library with manually working around that bug,
					// this library automatically caps the returned delay to a maximum value of about `24.85` days
					// which still works correctly with `setTimeout()` function and doesn't break it.
					//
					// The end user of this library could still disable this automatic workaround
					// by passing a `getTimeToNextUpdateUncapped: true` parameter.
					// In that case, it will return the original non-modified uncapped delay
					// which can be longer than `24.85` days and should be manually capped
					// by the developer if it's going to be used in a `setTimeout()` call.
					//
					if (options.getTimeToNextUpdateUncapped) {
						return timeToNextUpdate
					}
					return getSafeTimeoutDelay(timeToNextUpdate)
				}
			}

			return { getText, getTextRefreshDelay }
		}

		const { getText, getTextRefreshDelay } = getTextAndTextRefreshDelayGetterFunctions({ now })

		if (options.getTimeToNextUpdate) {
			return [getText(), getTextRefreshDelay()]
		}

		if (options.refresh) {
			// `getTextRefreshDelay()` will return `undefined` for a custom style
			// that doesn't meet the minimum requirements for this feature.
			// See the README for more details.
			//
			// This is a "sensible default" interval for refreshing time labels
			// that use such custom style.
			//
			const defaultRefreshInterval = 60 * 1000

			// If `refresh` function was passed, schedule it to be called when the time comes,
			// after which schedule the next refresh.
			let refreshTimer
			const scheduleRefresh = (delay = defaultRefreshInterval) => {
				refreshTimer = setTimeoutSafe(() => {
					const { getText, getTextRefreshDelay } = getTextAndTextRefreshDelayGetterFunctions({
						now: now + (Date.now() - nowRealWhenCalled)
					})
					options.refresh(getText())
					scheduleRefresh(getTextRefreshDelay())
				}, delay)
			}

			scheduleRefresh(getTextRefreshDelay())

			const stopRefreshing = () => {
				clearTimeout(refreshTimer)
			}

			return [getText(), stopRefreshing]
		}

		return getText()
	}

	formatDateForStep(timestamp, step, secondsPassed, {
		labels,
		labelsType,
		nowLabel,
		now,
		future,
		round
	}) {
		// If no step matches, then output an empty string.
		if (!step) {
			return
		}

		if (step.format) {
			return step.format(timestamp, this.locale, {
				formatAs: (unit, amount) => {
					// Mimicks `Intl.RelativeTimeFormat.format()`.
					return this.formatValue(amount, unit, {
						labels,
						future
					})
				},
				now,
				future
			})
		}

		// "unit" is now called "formatAs".
		const unit = step.unit || step.formatAs

		if (!unit) {
			throw new Error(`[javascript-time-ago] Each step must define either \`formatAs\` or \`format()\`. Step: ${JSON.stringify(step)}`)
		}

		// `Intl.RelativeTimeFormat` doesn't operate in "now" units.
		// Therefore, threat "now" as a special case.
		if (unit === 'now') {
			return nowLabel
		}

		// Amount in units.
		let amount = Math.abs(secondsPassed) / getStepDenominator(step)

		// Apply granularity to the time amount
		// (and fallback to the previous step
		//  if the first level of granularity
		//  isn't met by this amount)
		//
		// `granularity` — (advanced) Time interval value "granularity".
		// For example, it could be set to `5` for minutes to allow only 5-minute increments
		// when formatting time intervals: `0 minutes`, `5 minutes`, `10 minutes`, etc.
		// Perhaps this feature will be removed because there seem to be no use cases
		// of it in the real world.
		//
		if (step.granularity) {
			// Recalculate the amount of seconds passed based on granularity
			amount = getRoundFunction(round)(amount / step.granularity) * step.granularity
		}

		let valueForFormatting = -1 * Math.sign(secondsPassed) * getRoundFunction(round)(amount)

		// By default, this library formats a `0` in "past" mode,
		// unless `future: true` option is passed.
		// This is different to `relative-time-format`'s behavior
		// which formats a `0` in "future" mode by default, unless it's a `-0`.
		// So, convert `0` to `-0` if `future: true` option wasn't passed.
		// `=== 0` matches both `0` and `-0`.
		if (valueForFormatting === 0) {
			if (future) {
				valueForFormatting = 0
			} else {
				valueForFormatting = -0
			}
		}

		switch (labelsType) {
			case 'long':
			case 'short':
			case 'narrow':
				// Format the amount using `Intl.RelativeTimeFormat`.
				return this.getFormatter(labelsType).format(valueForFormatting, unit)
			default:
				// Format the amount.
				// (mimicks `Intl.RelativeTimeFormat` behavior for other time label styles)
				return this.formatValue(valueForFormatting, unit, {
					labels,
					future
				})
		}
	}

	/**
	 * Mimicks what `Intl.RelativeTimeFormat` does for additional locale styles.
	 * @param  {number} value
	 * @param  {string} unit
	 * @param  {object} options.labels — Relative time labels.
	 * @param  {boolean} [options.future] — Tells how to format value `0`: as "future" (`true`) or "past" (`false`). Is `false` by default, but should have been `true` actually.
	 * @return {string}
	 */
	formatValue(value, unit, { labels, future }) {
		return this.getFormattingRule(labels, unit, value, { future })
			.replace('{0}', this.formatNumber(Math.abs(value)))
	}

	/**
	 * Returns formatting rule for `value` in `units` (either in past or in future).
	 * @param {object} formattingRules — Relative time labels for different units.
	 * @param {string} unit - Time interval measurement unit.
	 * @param {number} value - Time interval value.
	 * @param  {boolean} [options.future] — Tells how to format value `0`: as "future" (`true`) or "past" (`false`). Is `false` by default.
	 * @return {string}
	 * @example
	 * // Returns "{0} days ago"
	 * getFormattingRule(en.long, "day", -2, 'en')
	 */
	getFormattingRule(formattingRules, unit, value, { future }) {
		// Passing the language is required in order to
		// be able to correctly classify the `value` as a number.
		const locale = this.locale
		formattingRules = formattingRules[unit]
		// Check for a special "compacted" rules case:
		// if formatting rules are the same for "past" and "future",
		// and also for all possible `value`s, then those rules are
		// stored as a single string.
		if (typeof formattingRules === 'string') {
			return formattingRules
		}
		// Choose either "past" or "future" based on time `value` sign.
		// If "past" is same as "future" then they're stored as "other".
		// If there's only "other" then it's being collapsed.
		const pastOrFuture = value === 0 ? (future ? 'future' : 'past') : (value < 0 ? 'past' : 'future')
		const quantifierRules = formattingRules[pastOrFuture] || formattingRules
		// Bundle size optimization technique.
		if (typeof quantifierRules === 'string') {
			return quantifierRules
		}
		// Quantify `value`.
		const quantifier = this.getPluralRules().select(Math.abs(value))
		// "other" rule is supposed to always be present.
		// If only "other" rule is present then "rules" is not an object and is a string.
		return quantifierRules[quantifier] || quantifierRules.other
	}

	/**
	 * Formats a number into a string.
	 * Uses `Intl.NumberFormat` when available.
	 * @param  {number} number
	 * @return {string}
	 */
	formatNumber(number) {
		return this.numberFormat ? this.numberFormat.format(number) : String(number)
	}

	/**
	 * Returns an `Intl.RelativeTimeFormat` for a given `labelsType`.
	 * @param {string} labelsType
	 * @return {object} `Intl.RelativeTimeFormat` instance
	 */
	getFormatter(labelsType) {
		// `Intl.RelativeTimeFormat` instance creation is (hypothetically) assumed
		// a lengthy operation so the instances are cached and reused.
		return this.relativeTimeFormatCache.get(this.locale, labelsType) ||
			this.relativeTimeFormatCache.put(this.locale, labelsType, new this.IntlRelativeTimeFormat(this.locale, { style: labelsType }))
	}

	/**
	 * Returns an `Intl.PluralRules` instance.
	 * @return {object} `Intl.PluralRules` instance
	 */
	getPluralRules() {
		// `Intl.PluralRules` instance creation is (hypothetically) assumed
		// a lengthy operation so the instances are cached and reused.
		return this.pluralRulesCache.get(this.locale) ||
			this.pluralRulesCache.put(this.locale, new this.IntlPluralRules(this.locale))
	}


	/**
	 * Gets localized labels for this type of labels.
	 *
	 * @param {(string|string[])} labelsType - Relative date/time labels type.
	 *                                     If it's an array then all label types are tried
	 *                                     until a suitable one is found.
	 *
	 * @returns {Object} Returns an object of shape { labelsType, labels }
	 */
	getLabels(labelsType = []) {
		// Convert `labels` to an array.
		if (typeof labelsType === 'string') {
			labelsType = [labelsType]
		}

		// Supports legacy "tiny" and "mini-time" label styles.
		labelsType = labelsType.map((labelsType) => {
			switch (labelsType) {
				case 'tiny':
				case 'mini-time':
					return 'mini'
				default:
					return labelsType
			}
		})

		// "long" labels type is the default one.
		// (it's always present for all languages)
		labelsType = labelsType.concat('long')

		// Find a suitable labels type.
		const localeData = getLocaleData(this.locale)
		for (const _labelsType of labelsType) {
			if (localeData[_labelsType]) {
				return {
					labelsType: _labelsType,
					labels: localeData[_labelsType]
				}
			}
		}
	}
}

/**
 * Default locale global variable.
 */
let defaultLocale = 'en'

/**
 * Gets default locale.
 * @return  {string} locale
 */
TimeAgo.getDefaultLocale = () => defaultLocale

/**
 * Sets default locale.
 * @param  {string} locale
 */
TimeAgo.setDefaultLocale = (locale) => {
	defaultLocaleHasBeenSpecified = true
	defaultLocale = locale
}

/**
 * Adds locale data for a specific locale and marks the locale as default.
 * @param {Object} localeData
 */
TimeAgo.addDefaultLocale = function(localeData) {
	// Warn the user if they've previously already added a default locale (a different one).
	if (defaultLocaleHasBeenSpecified) {
		if (TimeAgo.getDefaultLocale() !== localeData.locale) {
			console.warn(`[javascript-time-ago] You're adding "${localeData.locale}" as the default locale but you have already added "${TimeAgo.getDefaultLocale()}" as the default locale. "${localeData.locale}" is the default locale now.`)
		}
	}
	defaultLocaleHasBeenSpecified = true

	// `addDefaultLocale()` is just a shortcut to `addLocale()` + `setDefaultLocale()`.
	TimeAgo.addLocale(localeData)
	TimeAgo.setDefaultLocale(localeData.locale)
}

let defaultLocaleHasBeenSpecified = false

/**
 * Adds locale data for a specific locale.
 * @param {Object} localeData
 */
TimeAgo.addLocale = function(localeData) {
	addLocaleData(localeData)
	RelativeTimeFormatPolyfill.addLocale(localeData)
}

/**
 * (legacy alias)
 * Adds locale data for a specific locale.
 * @param {Object} localeData
 * @deprecated
 */
TimeAgo.locale = TimeAgo.addLocale

/**
 * Adds custom labels to locale data.
 * @param {string} locale
 * @param {string} name
 * @param {object} labels
 */
TimeAgo.addLabels = (locale, name, labels) => {
	let localeData = getLocaleData(locale)
	if (!localeData) {
		addLocaleData({
			locale
		})
		localeData = getLocaleData(locale)
		// throw new Error(`[javascript-time-ago] No data for locale "${locale}"`)
	}
	localeData[name] = labels
}

// Normalizes `.format()` `time` argument.
function getTimestamp(input) {
	if (input.constructor === Date || isMockedDate(input)) {
		return input.getTime()
	}

	if (typeof input === 'number') {
		return input
	}

	// For some weird reason istanbul doesn't see this `throw` covered.
	/* istanbul ignore next */
	throw new Error(`Unsupported relative time formatter input: ${typeof input}, ${input}`)
}

// During testing via some testing libraries `Date`s aren't actually `Date`s.
// https://github.com/catamphetamine/javascript-time-ago/issues/22
function isMockedDate(object) {
	return typeof object === 'object' && typeof object.getTime === 'function'
}

// Get available time interval measurement units.
function getTimeIntervalMeasurementUnits(allowedUnits, labels, nowLabel) {
	// Get all time interval measurement units that're available
	// in locale data for a given time labels style.
	let units = Object.keys(labels)

	// `now` unit is handled separately and is shipped in its own `now.json` file.
	// `now.json` isn't present for all locales, so it could be substituted with
	// ".second.current".
	// Add `now` unit if it's available in locale data.
	if (nowLabel) {
		units.push('now')
	}

	// If only a specific set of available time measurement units can be used
	// then only those units are allowed (if they're present in locale data).
	if (allowedUnits) {
		units = allowedUnits.filter(unit => unit === 'now' || units.indexOf(unit) >= 0)
	}

	return units
}

function getNowLabel(labels, nowLabels, longLabels, future) {
	const nowLabel = labels.now || (nowLabels && nowLabels.now)
	// Specific "now" message form extended locale data (if present).
	if (nowLabel) {
		// Bundle size optimization technique.
		if (typeof nowLabel === 'string') {
			return nowLabel
		}
		// Not handling `value === 0` as `localeData.now.current` here
		// because it wouldn't make sense: "now" is a moment,
		// so one can't possibly differentiate between a
		// "previous" moment, a "current" moment and a "next moment".
		// It can only be differentiated between "past" and "future".
		if (future) {
			return nowLabel.future
		} else {
			return nowLabel.past
		}
	}
	// Use ".second.current" as "now" message.
	if (longLabels && longLabels.second && longLabels.second.current) {
		return longLabels.second.current
	}
}

function isStyle(variable) {
	return typeof variable === 'string' || isStyleObject(variable)
}

// `setTimeout()` function has a bug when it fires immediately
// when the delay is longer than about `24.85` days.
// https://stackoverflow.com/questions/3468607/why-does-settimeout-break-for-large-millisecond-delay-values
//
// Since `renderLabel()` function uses `setTimeout()` for recursion,
// that would mean infinite recursion.
//
// `setTimeoutSafe()` function works around that bug
// by capping the delay at the maximum allowed value.
//
function setTimeoutSafe(func, delay) {
  return setTimeout(func, getSafeTimeoutDelay(delay))
}
function getSafeTimeoutDelay(delay) {
  return Math.min(delay, SET_TIMEOUT_MAX_SAFE_DELAY)
}
const SET_TIMEOUT_MAX_SAFE_DELAY = 2147483647